import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, MapPin, ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import { CONTACT, IMAGES } from "../data/site";

function useTypewriter(text, speed = 42, startDelay = 500) {
  const [out, setOut] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setOut(text);
      setDone(true);
      return;
    }
    setOut("");
    setDone(false);
    let i = 0;
    let interval;
    const start = setTimeout(() => {
      interval = setInterval(() => {
        i += 1;
        setOut(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(interval);
          setDone(true);
        }
      }, speed);
    }, startDelay);
    return () => {
      clearTimeout(start);
      clearInterval(interval);
    };
  }, [text, speed, startDelay]);
  return { out, done };
}

function renderTyped(typed) {
  const key = "Casa Romano";
  const idx = typed.indexOf(key);
  if (idx === -1) return typed;
  return (
    <>
      {typed.slice(0, idx)}
      <span className="italic text-marigold">{key}</span>
      {typed.slice(idx + key.length)}
    </>
  );
}

const PHRASE_VARIANTS = ["Casa Romano", "Romano's House"];

// Cycles a phrase with a delete/retype typewriter loop once `active`.
function usePhraseCycler(variants, active, { typeSpeed = 80, deleteSpeed = 45, hold = 2000 } = {}) {
  const [text, setText] = useState(variants[0]);
  useEffect(() => {
    if (!active) {
      setText(variants[0]);
      return;
    }
    let cancelled = false;
    let timer;
    const wait = (ms) => new Promise((r) => { timer = setTimeout(r, ms); });
    const run = async () => {
      let vi = 0;
      while (!cancelled) {
        await wait(hold);
        if (cancelled) return;
        const current = variants[vi];
        for (let i = current.length; i >= 0; i--) {
          if (cancelled) return;
          setText(current.slice(0, i));
          await wait(deleteSpeed);
        }
        vi = (vi + 1) % variants.length;
        const nextV = variants[vi];
        for (let i = 0; i <= nextV.length; i++) {
          if (cancelled) return;
          setText(nextV.slice(0, i));
          await wait(typeSpeed);
        }
      }
    };
    run();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [active, variants, typeSpeed, deleteSpeed, hold]);
  return text;
}


const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.4 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const { openBooking } = useBooking();
  const { out, done } = useTypewriter(t.hero.titleTW);
  const [reduce] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const phrase = usePhraseCycler(PHRASE_VARIANTS, done && !reduce);
  const [pre, post = ""] = t.hero.titleTW.split("Casa Romano");

  return (
    <section
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden bg-purpleink-deep grain"
      data-testid="hero-section"
    >
      {/* Background image with parallax */}
      <motion.div
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={IMAGES.heroBeach}
          className="h-full w-full object-cover object-[center_25%]"
          data-testid="hero-video"
        >
          <source src={IMAGES.heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-purpleink-deep/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-purpleink-deep via-purpleink-deep/10 to-purpleink-deep/50" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-5 pb-16 pt-32 sm:px-8 sm:pb-24">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="mb-6 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-marigold sm:text-sm"
          data-testid="hero-kicker"
        >
          <span className="h-px w-10 bg-marigold" />
          {t.hero.kicker}
        </motion.span>

        <h1
          className="max-w-4xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
          data-testid="hero-title"
        >
          {done ? (
            <>
              {pre}
              <span className="italic text-marigold" data-testid="hero-cycle-phrase">
                {phrase}
              </span>
              {!reduce && (
                <span
                  aria-hidden="true"
                  className="cr-cursor ml-0.5 inline-block w-[3px] translate-y-1 bg-marigold align-middle"
                  style={{ height: "0.9em" }}
                />
              )}
              {post}
            </>
          ) : (
            <>
              {renderTyped(out)}
              <span
                aria-hidden="true"
                className="cr-cursor ml-1 inline-block w-[3px] translate-y-1 self-center bg-marigold align-middle"
                style={{ height: "0.9em" }}
              />
            </>
          )}
        </h1>

        <motion.p
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mt-8 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg"
          data-testid="hero-subtitle"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => openBooking("")}
            className="rounded-full bg-marigold px-8 py-4 text-base font-semibold text-purpleink-deep shadow-xl transition-transform duration-200 hover:bg-marigold-bright active:scale-95"
          >
            {t.hero.book}
          </button>
          <a
            data-testid="hero-directions-btn"
            href={CONTACT.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/10"
          >
            <MapPin className="h-5 w-5" />
            {t.hero.directions}
          </a>
        </motion.div>

        <motion.div
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mt-10 flex items-center gap-3"
          data-testid="hero-rating"
        >
          <div className="flex items-center gap-0.5">
            {[0, 1, 2, 3, 4].map((s) => (
              <Star
                key={s}
                className={`h-4 w-4 ${s < 4 ? "fill-marigold text-marigold" : "fill-marigold/40 text-marigold/40"}`}
              />
            ))}
          </div>
          <span className="text-sm font-medium text-white">
            {CONTACT.rating} · {CONTACT.reviewCount} {t.hero.ratingLabel}
          </span>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-2 text-white/60 sm:flex"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
};
